// 58 prioritised Adelaide suburb landing pages, each with genuine local context.
// Used by SuburbDetail.jsx to render genuinely unique pages — not templated doorways.

export const suburbsData = [
  // ------------------------------------------------------------------
  // NORTH
  // ------------------------------------------------------------------
  {
    name: "Mawson Lakes", slug: "mawson-lakes", postcode: "5095", region: "North",
    landmarks: ["Main North Rd", "UniSA Campus", "Elder Smith Rd"],
    intro: "Mawson Lakes is a tech-forward neighbourhood — university students, young professionals, and owners of modern multi-storey townhomes. The most common calls we attend here are around robust Wi-Fi coverage across split-level layouts and reliable home-office networks for remote workers.",
    commonFixes: [
      "Mesh Wi-Fi for double-storey townhouses",
      "Student laptop diagnostics & data recovery",
      "Home-office network setups for remote work",
      "Multi-device Wi-Fi & printer troubleshooting",
    ],
    focus: "wifi-troubleshooting",
  },
  {
    name: "Golden Grove", slug: "golden-grove", postcode: "5125", region: "North",
    landmarks: ["The Grove Way", "Golden Grove Village"],
    intro: "Golden Grove is family-suburb territory — multi-storey homes, home offices tucked into spare bedrooms, and Wi-Fi dead zones in the far corners of the property. We attend a lot of secondary-router and signal-extension jobs here, plus general family-PC tune-ups.",
    commonFixes: [
      "Eliminating Wi-Fi dead zones across larger homes",
      "Home-business setups and small-network configuration",
      "Slow PC tune-ups for the family laptop",
      "Setting up shared printers and network drives",
    ],
    focus: "wifi-troubleshooting",
  },
  {
    name: "Greenwith", slug: "greenwith", postcode: "5125", region: "North",
    landmarks: ["Target Hill Rd", "Reuben Richardson Rd"],
    intro: "Greenwith is established-family-home country — multiple kids, multiple devices, a houseful of devices, and at least one home printer that mysteriously stops working twice a year. We're the team that gets called when no one can stream Netflix and the laptop won't connect.",
    commonFixes: [
      "Family home network audits & speed optimisation",
      "Multi-device Wi-Fi troubleshooting",
      "Printer & shared-storage troubleshooting",
      "Laptop battery and slow-startup repairs",
    ],
    focus: "tech-support",
  },
  {
    name: "Salisbury", slug: "salisbury", postcode: "5108", region: "North",
    landmarks: ["Salisbury Highway", "Commercial Rd", "Parabanks Shopping Centre"],
    intro: "Salisbury has a strong mix of established residential streets and local trade businesses operating out of home offices and small commercial sites. We support both — fixing the workshop laptop in the morning, then heading down the road to help a senior recover from a phishing email in the afternoon.",
    commonFixes: [
      "Budget-friendly desktop repairs and tune-ups",
      "Email account recovery & spam filtering",
      "Anti-scam training for older family members",
    ],
    focus: "computer-repairs",
  },
  {
    name: "Elizabeth", slug: "elizabeth", postcode: "5112", region: "North",
    landmarks: ["Philip Highway", "Elizabeth Shopping Centre"],
    intro: "Elizabeth blends a long commercial/manufacturing corridor with established residential streets. We get a lot of calls for legacy desktop machines that 'just need to last a bit longer' and home-office network optimisation for shift workers.",
    commonFixes: [
      "Legacy desktop revival and Windows 10/11 upgrades",
      "Affordable hardware diagnosis (HDD/SSD swaps)",
      "Home internet & router optimisation",
      "Light commercial / shopfront network help",
    ],
    focus: "computer-repairs",
  },
  {
    name: "Gawler", slug: "gawler", postcode: "5118", region: "North",
    landmarks: ["Murray St", "Xavier College", "Northern Expressway"],
    intro: "Gawler is a semi-rural hub at the top of the metro — independent retailers, family-run cafes, and a sizeable retirement demographic. Anti-scam education and secure setup for older residents is a big part of what we do up here, alongside POS and small-business network work along Murray St.",
    commonFixes: [
      "Anti-scam coaching & device lockdown for seniors",
      "Home computer repairs in Gawler / Willaston",
      "Backup and data recovery for small businesses",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Parafield Gardens", slug: "parafield-gardens", postcode: "5107", region: "North",
    landmarks: ["Kings Rd", "Martins Rd"],
    intro: "Parafield Gardens is busy-family-suburb central — kids on iPads, parents on laptops, streaming in every room. Multi-device congestion on home Wi-Fi is our #1 call here, plus printer setups and the occasional locked-out email account.",
    commonFixes: [
      "Multi-device Wi-Fi congestion fixes",
      "Wireless printer pairing & network printing setup",
      "Locked Microsoft/Apple ID account recovery",
      "Family device parental-control configuration",
    ],
    focus: "wifi-troubleshooting",
  },
  {
    name: "Modbury", slug: "modbury", postcode: "5092", region: "North",
    landmarks: ["North East Rd", "Westfield Tea Tree Plaza"],
    intro: "Modbury mixes a high-volume retail district with surrounding residential streets — that's a perfect storm for both shopfront network rescues and same-day home tech-support calls. We're dispatched here often, especially around the Westfield retail corridor.",
    commonFixes: [
      "Retail and shopfront network rescue",
      "Same-day on-site IT dispatch across Modbury",
      "Home office and broadband setup help",
    ],
    focus: "tech-support",
  },
  {
    name: "Tea Tree Gully", slug: "tea-tree-gully", postcode: "5091", region: "North",
    landmarks: ["Haines Rd", "Memorial Drive"],
    intro: "Tea Tree Gully is leafy, established, and home to a sizeable older demographic. We handle a lot of in-home tech audits, anti-scam coaching, and home-office setups for residents looking after parents or working from home themselves.",
    commonFixes: [
      "In-home tech audits and device safety reviews",
      "Anti-scam coaching for older residents",
      "Home office network and printer setup",
      "iPad / iPhone / Apple ID assistance",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Burton", slug: "burton", postcode: "5110", region: "North",
    landmarks: ["Waterloo Corner Rd", "Port Wakefield Rd"],
    intro: "Burton sits along the semi-industrial fringe of the north — a strong mix of logistics businesses needing reliable data security and family homes needing reliable internet. We work both sides of that street.",
    commonFixes: [
      "Logistics / yard-office data protection & backups",
      "Secure remote-access setup for site managers",
      "Family home Wi-Fi & device support",
      "Email and Microsoft 365 troubleshooting",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Craigmore", slug: "craigmore", postcode: "5114", region: "North",
    landmarks: ["Yorktown Rd", "Hanson Rd"],
    intro: "Craigmore is sprawling-residential-family territory — large blocks, multiple kids, lots of devices, and demand for proper consumer hardware diagnostics. 'My laptop is slow' and 'NBN is dropping out again' are our two most common dispatch reasons.",
    commonFixes: [
      "Consumer hardware diagnosis & replacement",
      "Internet speed and NBN drop-out fixes",
      "Family PC tune-ups and malware removal",
      "Home-office connection stabilisation",
    ],
    focus: "computer-repairs",
  },
  {
    name: "Munno Para", slug: "munno-para", postcode: "5115", region: "North",
    landmarks: ["Main North Rd", "Curtis Rd"],
    intro: "Munno Para is one of the fastest-growing new-build estates in metro Adelaide. New houses, fresh NBN connections, and brand-new devices that need to be set up properly. We're often first on the scene for setup, configuration, and connection issues in these new developments.",
    commonFixes: [
      "New-build NBN connection & router setup",
      "New-device setup & configuration",
      "Whole-home mesh Wi-Fi installs",
      "Devices configuration for first-time owners",
    ],
    focus: "wifi-troubleshooting",
  },
  {
    name: "Ingle Farm", slug: "ingle-farm", postcode: "5098", region: "North",
    landmarks: ["Montague Rd", "Fairfax Rd"],
    intro: "Ingle Farm is an established residential pocket — homes that have been on the network for a while, computers that are due an update, and routers that should have been replaced two years ago. We're the team that quietly modernises the whole setup without breaking what works.",
    commonFixes: [
      "Anti-malware and security cleanup",
      "Old PC upgrades — SSDs, RAM, Windows refresh",
      "Outdated router & modem replacement",
      "Backup migration to a new computer",
    ],
    focus: "computer-repairs",
  },
  {
    name: "Pooraka", slug: "pooraka", postcode: "5095", region: "North",
    landmarks: ["Bridge Rd", "Research Rd", "Adelaide Produce Markets"],
    intro: "Pooraka is one of the city's main wholesale / logistics hubs — there are dozens of small B2B businesses here that need their networks to just work. Continuous patching, MFA rollouts, and ransomware-safe backup setups are our core work in Pooraka.",
    commonFixes: [
      "Wholesale/logistics network reliability",
      "Multi-factor authentication rollouts",
      "Backup strategy & ransomware preparation",
      "Office-and-warehouse Wi-Fi bridging",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Enfield", slug: "enfield", postcode: "5085", region: "North",
    landmarks: ["Main North Rd", "Regency Rd"],
    intro: "Enfield is an up-and-coming corridor — micro-businesses opening in small commercial spaces, plus a steady demand for residential software fixes from the surrounding streets. We do a lot of one-person-business IT setup work here.",
    commonFixes: [
      "Single-person business IT setup",
      "Domain email & custom mailbox configuration",
      "Residential software fixes and Windows reinstalls",
      "Light cyber-security baseline for new businesses",
    ],
    focus: "email-solutions",
  },

  // ------------------------------------------------------------------
  // EAST & HILLS
  // ------------------------------------------------------------------
  {
    name: "Norwood", slug: "norwood", postcode: "5067", region: "East & Hills",
    landmarks: ["The Parade", "Magill Rd"],
    intro: "Norwood is creative-and-medical-practice territory — design agencies on The Parade, medical clinics and allied-health rooms across the suburb, and high expectations for both data privacy and Wi-Fi that just works during a client appointment.",
    commonFixes: [
      "Enterprise-grade Wi-Fi for clinics and agencies",
      "Strict data-protection & MFA rollouts",
      "Secure backup configuration for client files",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Burnside", slug: "burnside", postcode: "5066", region: "East & Hills",
    landmarks: ["Greenhill Rd", "Burnside Village"],
    intro: "Burnside is premium residential — affluent retirees, work-from-home professionals, and homes with serious technology setups. We're frequently called in for quiet, professional in-home computer help, Wi-Fi improvements and device security checks.",
    commonFixes: [
      "Discreet in-home executive office setups",
      "Premium home cyber-security & MFA configuration",
      "Anti-scam education for retiree households",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Magill", slug: "magill", postcode: "5072", region: "East & Hills",
    landmarks: ["Magill Rd", "Penfold Rd", "UniSA Magill"],
    intro: "Magill straddles students and working-from-home academics — that means laptop emergencies the night before a deadline, plus serious home-office network configuration jobs for researchers and lecturers.",
    commonFixes: [
      "Student laptop emergency repairs",
      "Home-office network configuration for academics",
      "Cloud-storage and VPN setup",
      "Apple Mac and iPad troubleshooting",
    ],
    focus: "tech-support",
  },
  {
    name: "Campbelltown", slug: "campbelltown", postcode: "5074", region: "East & Hills",
    landmarks: ["Lower North East Rd", "Darley Rd"],
    intro: "Campbelltown is changing fast — denser residential development, more trade offices, more family tech support. We're called for everything from a fresh-build Wi-Fi setup to a sole-trader bookkeeping computer that won't boot.",
    commonFixes: [
      "Fresh-build home network setup",
      "Trade-business invoicing PC repairs",
      "Apple ID / iCloud account recovery",
      "General family tech support",
    ],
    focus: "tech-support",
  },
  {
    name: "Stirling", slug: "stirling", postcode: "5152", region: "East & Hills",
    landmarks: ["Mount Barker Rd", "South Eastern Freeway"],
    intro: "Stirling is Adelaide Hills boutique country — character homes with thick stone walls, premium properties, and businesses that demand high-end mesh Wi-Fi configurations to push a signal through it all.",
    commonFixes: [
      "Mesh Wi-Fi through stone & double-brick walls",
      "Outdoor access points for large properties",
      "Boutique-business network design",
      "Backup connectivity (4G failover) for unstable lines",
    ],
    focus: "wifi-troubleshooting",
  },
  {
    name: "Mount Barker", slug: "mount-barker", postcode: "5251", region: "East & Hills",
    landmarks: ["Adelaide Rd", "Druids Ave"],
    intro: "Mount Barker is a rapidly expanding regional hub — local storefronts, growing trade businesses, and remote-work corporate setups that need secure VPN access back to a head office in the city.",
    commonFixes: [
      "Storefront and shopfront network rescue",
      "Multi-site backup and data protection",
      "Home-office hardware repair",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Kensington", slug: "kensington", postcode: "5068", region: "East & Hills",
    landmarks: ["Kensington Rd", "Portrush Rd"],
    intro: "Kensington is character-home territory — older properties with quirky wiring, a high concentration of premium residential clients, and demand for secure data backups before the next family-photo archive goes missing.",
    commonFixes: [
      "Premium in-home tech support",
      "Secure backup design (off-site + cloud)",
      "Wi-Fi planning for heritage homes",
      "Apple ecosystem (Mac/iPad/iPhone) support",
    ],
    focus: "tech-support",
  },
  {
    name: "Unley", slug: "unley", postcode: "5061", region: "East & Hills",
    landmarks: ["Unley Rd", "King William Rd"],
    intro: "Unley is high-street retail and professional services — accountants, lawyers, hairdressers and cafes lining King William Rd and Unley Rd. We're the team that's dispatched same-day when the printer, EFTPOS or office Wi-Fi fall over.",
    commonFixes: [
      "Same-day on-site printer & network rescue",
      "Professional-services data protection",
    ],
    focus: "tech-support",
  },
  {
    name: "Malvern", slug: "malvern", postcode: "5061", region: "East & Hills",
    landmarks: ["Duthy St", "Cambridge Terrace"],
    intro: "Malvern is affluent historic-home country — gorgeous properties, full of devices, and homeowners who want a proper cyber-security audit (not a Best Buy sales pitch).",
    commonFixes: [
      "Home office & device security audits",
      "Premium cyber-awareness audits",
      "Whole-home Wi-Fi troubleshooting across large properties",
      "Secure family file sharing & backups",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Marden", slug: "marden", postcode: "5070", region: "East & Hills",
    landmarks: ["Payneham Rd", "Lower Portrush Rd"],
    intro: "Marden is urban-professional territory — dense, walkable, multi-device households where the Wi-Fi is asked to do a lot. We handle wireless network upgrades and small business setups across Payneham Rd.",
    commonFixes: [
      "Wi-Fi network upgrades for multi-device homes",
      "Home-business setup & support",
      "Streaming & Wi-Fi connectivity issues",
      "Apple device management",
    ],
    focus: "wifi-troubleshooting",
  },
  {
    name: "Rostrevor", slug: "rostrevor", postcode: "5073", region: "East & Hills",
    landmarks: ["Stradbroke Rd", "Morialta Conservation Park"],
    intro: "Rostrevor is family-heavy, with a private-school catchment and high device-per-household counts. Student laptops, gaming PCs, and home network reliability are our most common call-outs in this pocket.",
    commonFixes: [
      "Student laptop diagnostics and recovery",
      "Gaming PC performance and overheating fixes",
      "Family Wi-Fi reliability tuning",
      "Microsoft 365 / school account help",
    ],
    focus: "tech-support",
  },
  {
    name: "Athelstone", slug: "athelstone", postcode: "5076", region: "East & Hills",
    landmarks: ["Gorge Rd", "Fox Avenue"],
    intro: "Athelstone is a quiet residential pocket with a long-established older demographic. Our priority here is anti-scam protection, MFA on banking accounts, and unhurried, jargon-free explanations.",
    commonFixes: [
      "Anti-scam education for seniors",
      "Banking MFA & email-security setup",
      "Tablet and iPhone tutoring",
      "Simple device clean-up after suspicious activity",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Payneham", slug: "payneham", postcode: "5070", region: "East & Hills",
    landmarks: ["Portrush Rd", "Payneham Rd"],
    intro: "Payneham is small light-commercial — local service providers, accountants, family-run shops along Portrush Rd. Data migration when an old PC dies is one of our most common Payneham jobs.",
    commonFixes: [
      "Data migration between old & new computers",
      "Small business email & domain setup",
      "On-site backup configuration",
    ],
    focus: "computer-repairs",
  },
  {
    name: "St Peters", slug: "st-peters", postcode: "5069", region: "East & Hills",
    landmarks: ["Stephen Terrace", "Hackney Rd"],
    intro: "St Peters is elite residential — work-from-home corporate executives with high expectations around uptime, network reliability and quiet, professional service. We're often the discreet team that quietly upgrades the whole setup over a weekend.",
    commonFixes: [
      "High-availability home network design",
      "Executive home-office IT support",
      "Premium Wi-Fi mesh installations",
      "Off-site backup configuration",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Aldgate", slug: "aldgate", postcode: "5154", region: "East & Hills",
    landmarks: ["Strathalbyn Rd", "Mount Barker Rd"],
    intro: "Aldgate is Hills residential — complex property structures, separate studios, granny flats, and the kind of acreage where Wi-Fi normally gives up two-thirds of the way to the shed. Point-to-point bridging and outdoor APs are our bread and butter here.",
    commonFixes: [
      "Point-to-point Wi-Fi bridging to outbuildings",
      "Outdoor access points & mesh expansion",
      "Internet failover (4G backup) for storm-prone areas",
      "Whole-home Wi-Fi troubleshooting across large properties",
    ],
    focus: "wifi-troubleshooting",
  },

  // ------------------------------------------------------------------
  // WEST & CBD
  // ------------------------------------------------------------------
  {
    name: "Adelaide CBD", slug: "adelaide-cbd", postcode: "5000", region: "West & CBD",
    landmarks: ["King William St", "Rundle Mall", "Gouger St"],
    intro: "The Adelaide CBD is full of apartments, home offices and busy residents who can't afford computer downtime. We do a lot of responsive work here — a laptop that won't start before a deadline, apartment Wi-Fi that drops out, or email that suddenly stops syncing.",
    commonFixes: [
      "CBD on-site IT dispatch",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Port Adelaide", slug: "port-adelaide", postcode: "5015", region: "West & CBD",
    landmarks: ["Commercial Rd", "Grand Junction Rd", "Techport"],
    intro: "Port Adelaide is shipping, maritime logistics, and industrial — businesses that absolutely need their data secure and their networks online. Cybersecurity framework rollouts and server reliability are core to what we do at the Port.",
    commonFixes: [
      "Maritime / logistics network reliability",
      "Cybersecurity framework rollouts",
      "Server backup and disaster recovery",
      "Secure remote-access for shore staff",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Glenelg", slug: "glenelg", postcode: "5045", region: "West & CBD",
    landmarks: ["Jetty Rd", "Anzac Highway"],
    intro: "Glenelg is the busy tourist-retail strip on the coast — cafes, medical practices, beachfront apartments. Fast retail Wi-Fi, EFTPOS reliability, and tidy in-apartment setups are the dominant call types here.",
    commonFixes: [
      "Retail and cafe Wi-Fi performance",
      "Beachfront apartment network setup",
      "Holiday-rental router & guest-Wi-Fi configuration",
    ],
    focus: "wifi-troubleshooting",
  },
  {
    name: "Henley Beach", slug: "henley-beach", postcode: "5022", region: "West & CBD",
    landmarks: ["Military Rd", "Seaview Rd"],
    intro: "Henley Beach is busy coastal commercial — restaurants and cafes lining the esplanade — paired with high-end residential. Secure home offices, reliable Wi-Fi and beach-side retail networks are our common Henley Beach work.",
    commonFixes: [
      "Coastal retail and hospitality Wi-Fi",
      "Secure home office setup",
      "Secure home office configuration",
      "Salt-air router replacement planning",
    ],
    focus: "wifi-troubleshooting",
  },
  {
    name: "West Lakes", slug: "west-lakes", postcode: "5021", region: "West & CBD",
    landmarks: ["West Lakes Boulevard", "Westfield West Lakes"],
    intro: "West Lakes mixes established residential — including a strong retiree demographic — with the Westfield retail precinct. We handle in-home tech support, anti-scam coaching, and shopfront IT rescues in equal measure.",
    commonFixes: [
      "In-home tech support for retirees",
      "Anti-scam coaching & device safety reviews",
      "Westfield-area retail network help",
      "Home office and printer setup",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Prospect", slug: "prospect", postcode: "5082", region: "West & CBD",
    landmarks: ["Prospect Rd", "Prospect Oval", "Churchill Rd"],
    intro: "Whether you're running a bustling creative agency along the vibrant Prospect Road strip or managing a high-demand work-from-home corporate office near Prospect Oval, Tech Emergency delivers rapid on-site IT support. We specialise in keeping Prospect's fast-paced business and residential tech connected — without the need for a physical repair shop.",
    commonFixes: [
      "Creative-agency network & NAS setup",
      "Urban home-office Wi-Fi tuning",
    ],
    focus: "computer-repairs",
  },
  {
    name: "Hindmarsh", slug: "hindmarsh", postcode: "5007", region: "West & CBD",
    landmarks: ["Port Rd", "South Rd"],
    intro: "Hindmarsh is light-industrial and commercial distribution — warehouses, small distributors, and trade offices. Reliable data networks and secure workstations are the bedrock of what we do here.",
    commonFixes: [
      "Warehouse and yard-office network reliability",
      "Secure workstation setup for distribution staff",
      "Backup & continuity planning",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Mile End", slug: "mile-end", postcode: "5031", region: "West & CBD",
    landmarks: ["South Rd", "Sir Donald Bradman Drive"],
    intro: "Mile End is close-to-city commercial — small offices, trade businesses, professional suites. Rapid emergency IT dispatch is our main service here; when something's down, you can't wait two days for the city firm.",
    commonFixes: [
      "Rapid same-day on-site IT dispatch",
      "Trade-business IT support",
      "Office network upgrades",
      "Email migration and Microsoft 365 setup",
    ],
    focus: "tech-support",
  },
  {
    name: "Croydon", slug: "croydon", postcode: "5008", region: "West & CBD",
    landmarks: ["Queen St", "Port Rd"],
    intro: "Croydon is artisan-retail and creative studios — Queen Street is a creative corridor. Many of our Croydon clients are home-based businesses that need secure cloud setups and reliable home Wi-Fi to run their craft.",
    commonFixes: [
      "Home-based business cloud setup",
      "Artisan/retail Wi-Fi for studios",
      "Secure file-sharing for makers",
      "Apple Mac and creative-software support",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Findon", slug: "findon", postcode: "5023", region: "West & CBD",
    landmarks: ["Findon Rd", "Grange Rd"],
    intro: "Findon is high-density residential — families, multi-generational households, lots of devices. Slow-computer optimisation and clearing out years of accumulated bloatware is one of our most common Findon jobs.",
    commonFixes: [
      "Slow PC tune-ups and bloatware removal",
      "Family device & parental-control setup",
      "Wi-Fi range fixes across larger homes",
      "Tablet & smartphone tutoring",
    ],
    focus: "computer-repairs",
  },
  {
    name: "Grange", slug: "grange", postcode: "5022", region: "West & CBD",
    landmarks: ["Grange Rd", "Military Rd"],
    intro: "Grange is seaside residential — coastal homes wanting their wireless printers and home networks to just work without the salt-air rust killing every router every two years.",
    commonFixes: [
      "Seaside-home Wi-Fi and salt-air planning",
      "Wireless printer & Wi-Fi troubleshooting",
      "Wireless printer pairing",
      "Home network audit and reconfiguration",
    ],
    focus: "tech-support",
  },
  {
    name: "Fulham", slug: "fulham", postcode: "5024", region: "West & CBD",
    landmarks: ["Tapleys Hill Rd", "Henley Beach Rd"],
    intro: "Fulham is established-family country with high device counts per household — at least two laptops, a couple of phones, a printer, and a long list of connected devices. Network reliability under load is our main job here.",
    commonFixes: [
      "High-device-count network optimisation",
      "Mesh Wi-Fi and dead-zone elimination",
      "Printer and shared-drive setup",
      "Backup configuration for family photos",
    ],
    focus: "wifi-troubleshooting",
  },
  {
    name: "Beverley", slug: "beverley", postcode: "5009", region: "West & CBD",
    landmarks: ["Port Rd", "East Ave"],
    intro: "Beverley is heavy-industrial and automotive — workshops, parts distributors, mechanics with computers running invoicing and job-tracking software. Localized B2B IT maintenance and cyber-security baseline work dominates our Beverley call sheet.",
    commonFixes: [
      "Workshop / automotive office IT maintenance",
      "Trade-business cybersecurity baseline",
      "Invoicing & MYOB / Xero PC support",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Torrensville", slug: "torrensville", postcode: "5031", region: "West & CBD",
    landmarks: ["Henley Beach Rd", "South Rd"],
    intro: "Torrensville is diverse multicultural retail and commercial — independent grocers, cafes, restaurants. Reliable network support for owner-operator shops with non-English-first owners is a common request, and we take time to explain everything plainly.",
    commonFixes: [
      "Independent grocer & cafe network support",
      "Bilingual-friendly device setup",
      "Email and domain configuration",
    ],
    focus: "tech-support",
  },
  {
    name: "Woodville", slug: "woodville", postcode: "5011", region: "West & CBD",
    landmarks: ["Port Rd", "Woodville Rd"],
    intro: "Woodville is the civic-services hub of the west — medical clinics, administrative offices, allied health rooms. High-uptime IT and strict patient-data protection are the bar we work to here.",
    commonFixes: [
      "Medical & allied-health network reliability",
      "Patient-data backup and protection",
      "Practice management software support",
      "Secure email and document handling",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Fitzroy", slug: "fitzroy", postcode: "5082", region: "West & CBD",
    landmarks: ["North Adelaide Parklands", "Torrens Road", "Walkerville border"],
    intro: "Serving the historic, premium residential streets bordering the North Adelaide parklands, Tech Emergency provides highly secure, professional mobile IT support directly to your home in Fitzroy. From securing executive home networks near Torrens Road to optimising multi-device family setups, we bring elite tech troubleshooting right to your door.",
    commonFixes: [
      "Executive home network security & MFA",
      "Multi-device family Wi-Fi optimisation",
      "Premium in-home tech support",
      "Heritage-home Wi-Fi planning",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Wingfield", slug: "wingfield", postcode: "5013", region: "West & CBD",
    landmarks: ["Grand Junction Road", "South Road Superway", "Hanson Road"],
    intro: "In the heart of Adelaide's premier industrial and logistics hub, IT downtime equals lost revenue. Tech Emergency specialises in emergency on-site network rescue and business cyber-security auditing for warehouses and commercial operations along Grand Junction Road and the South Road Superway corridor. No waiting lines — just rapid industrial-grade dispatch.",
    commonFixes: [
      "Emergency warehouse network rescue",
      "Industrial-scale cyber-security audits",
      "Logistics yard-office data protection",
      "Server backup and continuity planning",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Regency Park", slug: "regency-park", postcode: "5010", region: "West & CBD",
    landmarks: ["Regency Road", "TAFE SA Campus", "Days Road"],
    intro: "From the busy distribution hubs off Regency Road to commercial operations near the TAFE SA campus, Tech Emergency provides enterprise-scale on-site computer support and data protection. We ensure local commercial networks are optimised, secure, and fully aligned with modern cyber-security baselines.",
    commonFixes: [
      "Enterprise-scale on-site IT support",
      "Distribution-hub network optimisation",
      "Cyber-security baseline alignment",
      "Office-and-warehouse Wi-Fi bridging",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Semaphore", slug: "semaphore", postcode: "5019", region: "West & CBD",
    landmarks: ["Semaphore Road", "The Esplanade", "Semaphore Beach"],
    intro: "From apartments near Semaphore Road to family homes down by the Esplanade, Tech Emergency covers the coast — computer repairs, Wi-Fi fixes and anti-scam device protection at your place. Our fully mobile, police-checked technician handles your tech on-site.",
    commonFixes: [
      "Anti-scam device protection for Esplanade residents",
      "Coastal Wi-Fi planning (salt-air ready)",
      "Holiday-rental router & guest Wi-Fi setup",
    ],
    focus: "computer-repairs",
  },

  // ------------------------------------------------------------------
  // SOUTH
  // ------------------------------------------------------------------
  {
    name: "Marion", slug: "marion", postcode: "5043", region: "South",
    landmarks: ["Sturt Rd", "Morphett Rd", "Westfield Marion"],
    intro: "Marion is high-volume commercial and retail — anchored by Westfield Marion — plus dense surrounding residential streets and medical suites. We do a steady mix of shopfront IT rescue, medical-clinic network protection, and family home support here.",
    commonFixes: [
      "Westfield-precinct retail network rescue",
      "Medical suite network and data protection",
      "Family home tech support",
    ],
    focus: "tech-support",
  },
  {
    name: "Morphett Vale", slug: "morphett-vale", postcode: "5162", region: "South",
    landmarks: ["Main South Rd", "Beach Rd"],
    intro: "Morphett Vale is Adelaide's largest residential suburb by population — that means a massive volume of home computer repairs, family Wi-Fi setup, and slow-PC troubleshooting. We attend Morphett Vale every week.",
    commonFixes: [
      "Home computer repairs & tune-ups",
      "Family Wi-Fi setup and optimisation",
      "Slow PC speed-up and SSD upgrades",
      "Printer, scanner and device setup",
    ],
    focus: "computer-repairs",
  },
  {
    name: "Noarlunga Centre", slug: "noarlunga-centre", postcode: "5168", region: "South",
    landmarks: ["Beach Rd", "Dyson Rd", "Colonnades"],
    intro: "Noarlunga is the regional commercial hub of the south — legal and medical offices, community organisations, and family-run small businesses. Secure network and document handling is core to what they need from us.",
    commonFixes: [
      "Secure document handling for legal & medical",
      "Community-organisation IT and data management",
      "Small-business network configuration",
      "Backup and disaster recovery planning",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Hallett Cove", slug: "hallett-cove", postcode: "5158", region: "South",
    landmarks: ["Lonsdale Rd", "Hallett Cove Shopping Centre"],
    intro: "Hallett Cove is coastal family-home territory with tricky topography — cliffs, valleys, varied house orientations all conspire to create Wi-Fi dropout zones. Fixing those is most of what we do in Hallett Cove.",
    commonFixes: [
      "Coastal Wi-Fi signal dropout fixes",
      "Mesh network design for split-level homes",
      "Home-office network reliability",
      "Streaming & Wi-Fi troubleshooting",
    ],
    focus: "wifi-troubleshooting",
  },
  {
    name: "Aberfoyle Park", slug: "aberfoyle-park", postcode: "5159", region: "South",
    landmarks: ["Hub Drive", "Blacks Rd"],
    intro: "Aberfoyle Park is dense family suburbia — multiple kids, multiple student devices, multiple work-from-home parents. Wireless network expansion to cover the whole house is the most common job.",
    commonFixes: [
      "Wireless network expansion & mesh installs",
      "Student laptop and homework device support",
      "Family Wi-Fi optimisation under heavy load",
      "Printer and shared-storage setup",
    ],
    focus: "wifi-troubleshooting",
  },
  {
    name: "Brighton", slug: "brighton", postcode: "5048", region: "South",
    landmarks: ["Jetty Rd", "Brighton Rd"],
    intro: "Brighton blends seaside retail with high-income residential — cafes along Jetty Rd, beautiful homes nearby. Premium cyber-security setup for home offices is a frequent request.",
    commonFixes: [
      "Premium home-office cyber security",
      "Seaside cafe / retail Wi-Fi",
      "Apple ecosystem support",
      "Secure family file backups",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Edwardstown", slug: "edwardstown", postcode: "5039", region: "South",
    landmarks: ["South Rd", "Cross Rd"],
    intro: "Edwardstown is a major commercial / light industrial strip — small businesses, trade offices, and workshops. Small-business IT support, network cabling diagnostics, and data-protection work dominate our Edwardstown jobs.",
    commonFixes: [
      "Small business IT & network setup",
      "Data protection & backup strategy",
      "Trade office computer support",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Blackwood", slug: "blackwood", postcode: "5051", region: "South",
    landmarks: ["Main Rd", "Shepherds Hill Rd"],
    intro: "Blackwood is foothills community territory — local independent shops, home offices, and properties surrounded by tall gums that wreak havoc on Wi-Fi. We're often called to redesign the home network for terrain-affected signal.",
    commonFixes: [
      "Foothills Wi-Fi redesign for tree-affected signal",
      "Independent-shop network support",
      "Home-office connectivity",
      "Outdoor and detached-studio Wi-Fi bridging",
    ],
    focus: "wifi-troubleshooting",
  },
  {
    name: "Sturt", slug: "sturt", postcode: "5047", region: "South",
    landmarks: ["Sturt Rd", "Marion Rd"],
    intro: "Sturt is retail-adjacent residential — close to Flinders University, with a strong student presence. Student tech support and home internet optimisation are our most common Sturt jobs.",
    commonFixes: [
      "Flinders University student laptop support",
      "Home internet optimisation",
      "Assignment-emergency data recovery",
      "Apple ID / Microsoft account help",
    ],
    focus: "tech-support",
  },
  {
    name: "Plympton", slug: "plympton", postcode: "5038", region: "South",
    landmarks: ["Anzac Highway", "Marion Rd"],
    intro: "Plympton sits along a busy transport corridor — medical clinics, professional suites, and small-business offices. Secure on-site data handling and high-uptime networks are non-negotiable here.",
    commonFixes: [
      "Medical clinic data protection",
      "Professional-suite network setup",
      "Secure backup and recovery",
      "On-site network troubleshooting",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Mitcham", slug: "mitcham", postcode: "5062", region: "South",
    landmarks: ["Belair Rd", "Welbourne St"],
    intro: "Mitcham is high-end heritage — private school catchment, premium homes. Home office configurations and device security audits for executives are common; we also do plenty of home Wi-Fi and connectivity work.",
    commonFixes: [
      "Premium home office configuration",
      "Device security audits",
      "Apple ecosystem support & device security",
      "Heritage-home Wi-Fi design",
    ],
    focus: "anti-scamming-cyber-awareness",
  },
  {
    name: "Reynella", slug: "reynella", postcode: "5161", region: "South",
    landmarks: ["Old South Rd", "Reynell Rd"],
    intro: "Reynella mixes historic / established residential with small local trades. Consumer tech assistance — printer issues, slow laptops, email lockouts — is our regular Reynella work.",
    commonFixes: [
      "Printer & wireless device setup",
      "Slow laptop tune-ups",
      "Email account lockouts & recovery",
      "Small local trade IT support",
    ],
    focus: "tech-support",
  },
  {
    name: "Flagstaff Hill", slug: "flagstaff-hill", postcode: "5159", region: "South",
    landmarks: ["Blacks Rd", "Flagstaff Rd"],
    intro: "Flagstaff Hill is leafy residential estate territory — high-income households running multiple streaming and work devices at once. Optimised whole-home networks are what we deliver here.",
    commonFixes: [
      "Whole-home network optimisation",
      "Multi-device streaming reliability",
      "New-device setup & configuration",
      "Home office tech support",
    ],
    focus: "wifi-troubleshooting",
  },
];

// Helpful derived lookups
export const allSuburbsDetailed = suburbsData;
export const suburbsBySlug = Object.fromEntries(suburbsData.map((s) => [s.slug, s]));
export const suburbsByRegion = suburbsData.reduce((acc, s) => {
  (acc[s.region] = acc[s.region] || []).push(s);
  return acc;
}, {});
